import { useCallback } from 'react';
import { useActorRef, useSelector } from '@xstate5/react';

import { useForgotToScanSubmission } from '../hooks';
import { type ForgotToScanInput } from '../utils';
import { forgotToScanMachine } from './forgot-to-scan.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useForgotToScanMachine = () => {
  const { submitForm } = useForgotToScanSubmission();

  // ─── Machine ─────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    forgotToScanMachine.provide({ actors: { submitForm } }),
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isShowingLandingPage = useSelector(actorRef, (snapshot) => snapshot.matches('idle')); // prettier-ignore
  const isShowingForm = useSelector(actorRef, (snapshot) => snapshot.matches('form') || snapshot.matches('submitting')); // prettier-ignore
  const isSubmittingForm = useSelector(actorRef, (snapshot) => snapshot.matches('submitting')); // prettier-ignore
  const hasPointsBeenIssued = useSelector(actorRef, (snapshot) => snapshot.matches('pointsIssued')); // prettier-ignore
  const isTimeRestrictionError = useSelector(actorRef, (snapshot) => snapshot.matches('timeRestrictionError')); // prettier-ignore
  const isReceiptAlreadyClaimedError = useSelector(actorRef, (snapshot) => snapshot.matches('receiptAlreadyClaimedError')); // prettier-ignore
  const isTransactionNotFoundError = useSelector(actorRef, (snapshot) => snapshot.matches('transactionNotFoundError')); // prettier-ignore
  const isUnknownError = useSelector(actorRef, (snapshot) => snapshot.matches('unknownError')); // prettier-ignore
  const hasError = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.matches('timeRestrictionError') ||
      snapshot.matches('receiptAlreadyClaimedError') ||
      snapshot.matches('transactionNotFoundError') ||
      snapshot.matches('unknownError'),
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const goBack = useCallback(() => {
    actorRef.send({ type: 'GO_BACK' });
  }, [actorRef]);

  const showForm = useCallback(() => {
    actorRef.send({ type: 'SHOW_FORM' });
  }, [actorRef]);

  const requestSubmission = useCallback(
    (params: ForgotToScanInput) => {
      actorRef.send({ type: 'SUBMIT_FORM', params });
    },
    [actorRef],
  );

  // ─── Response ────────────────────────────────────────────────────────

  return {
    isShowingLandingPage,
    isShowingForm,
    isSubmittingForm,
    hasPointsBeenIssued,
    isTimeRestrictionError,
    isReceiptAlreadyClaimedError,
    isTransactionNotFoundError,
    isUnknownError,
    hasError,
    goBack,
    showForm,
    requestSubmission,
  };
};
